// ─── Step types ───────────────────────────────────────────────────────────────
// "action"      — user clicks the highlighted element to advance.
//                 Tutorial listens for a click on the target, then waits `delay`
//                 ms before advancing (so the click's natural action can resolve).
// "info"        — tooltip with a "Next" button. User reads and clicks Next.
// "navigate"    — no target. Tooltip shows an action button that navigates to
//                 `step.path` and advances.
// "wait"        — no tooltip. Polls silently for `step.target` to appear in the
//                 DOM, then auto-advances. Used after actions that open modals.
// "done"        — final celebration step. No target.
// ─────────────────────────────────────────────────────────────────────────────

export const PM_STEPS = [
  // ── Welcome ─────────────────────────────────────────────────────────────────
  {
    id: "welcome",
    type: "info",
    phase: null,
    target: null,
    title: "Hi! I'm Remi 👋",
    body: "I'll guide you through setting up your property management from scratch — step by step. It takes about 5 minutes. Let's go!",
    next: "Let's start!",
  },

  // ── Phase 1: Open the menu ───────────────────────────────────────────────────
  {
    id: "nav-menu-btn",
    type: "action",
    phase: "Open Menu",
    target: '[data-tutorial="nav-menu-btn"]',
    title: "Open the menu",
    body: "This button opens the main navigation. Tap it to continue.",
    delay: 350,
  },
  {
    id: "nav-pm-item",
    type: "action",
    phase: "Open Menu",
    target: '[data-tutorial="nav-pm-item"]',
    title: "Property Management",
    body: "Tap \"Property Management\" to open the registration form.",
    delay: 600,
  },

  // ── Phase 2: Create company form (on Dashboard modal) ────────────────────────
  {
    id: "mgmt-company-name",
    type: "info",
    phase: "Register Company",
    target: '[data-tutorial="mgmt-company-name"]',
    title: "Company Name",
    body: "Type your company's official name here. This will appear on all invoices and statements.",
    next: "Got it →",
  },
  {
    id: "mgmt-telephone",
    type: "info",
    phase: "Register Company",
    target: '[data-tutorial="mgmt-telephone"]',
    title: "Phone Number",
    body: "Your company's main phone number. Tenants and landlords may use this to reach you.",
    next: "Got it →",
  },
  {
    id: "mgmt-office",
    type: "info",
    phase: "Register Company",
    target: '[data-tutorial="mgmt-office"]',
    title: "Office Address",
    body: "Where is your office located? Enter the physical address.",
    next: "Got it →",
  },
  {
    id: "mgmt-position",
    type: "info",
    phase: "Register Company",
    target: '[data-tutorial="mgmt-position"]',
    title: "Your Position",
    body: "What is your role or title? For example: Manager, Director, Owner.",
    next: "Got it →",
  },
  {
    id: "mgmt-owner",
    type: "info",
    phase: "Register Company",
    target: '[data-tutorial="mgmt-owner"]',
    title: "Owner Name(s)",
    body: "Enter the full name of the company owner. If there are multiple owners, list them all here.",
    next: "Got it →",
  },
  {
    id: "mgmt-submit",
    type: "action",
    phase: "Register Company",
    target: '[data-tutorial="mgmt-submit"]',
    title: "Create Your Company!",
    body: "Fill in all the fields above, then click this button. Your account will be upgraded to Property Manager automatically.",
    delay: 1200,
  },

  // ── Transition: go to FRP module ─────────────────────────────────────────────
  {
    id: "go-to-frp",
    type: "navigate",
    phase: "Add Property",
    target: null,
    path: "/app/frp",
    title: "Company registered! 🎉",
    body: "Excellent work! Your company is set up. Now let's head to the Property Management module to add your first property.",
    next: "Take me there →",
  },

  // ── Intermediate: pick the Property Management role ──────────────────────────
  {
    id: "role-pm-btn",
    type: "action",
    phase: "Add Property",
    target: '[data-tutorial="role-pm-btn"]',
    title: "Select Property Management",
    body: "Tap \"Property Management\" to open your management dashboard.",
    delay: 600,
  },

  // ── Intermediate: select the company to reveal Add Property ─────────────────
  {
    id: "select-company",
    type: "action",
    phase: "Add Property",
    target: '[data-tutorial="company-card"]',
    title: "Your Company is Here!",
    body: "Your management company has been created. Click \"Open/View\" to enter it and start adding properties.",
    delay: 800,
  },

  // ── Phase 3: Add property ────────────────────────────────────────────────────
  {
    id: "add-property-btn",
    type: "action",
    phase: "Add Property",
    target: '[data-tutorial="add-property-btn"]',
    title: "Add a Property",
    body: "This is where you add a building or complex. Click \"Add Property\" to begin.",
    delay: 600,
  },
  {
    id: "property-landlord",
    type: "info",
    phase: "Add Property",
    target: '[data-tutorial="property-landlord"]',
    title: "Select the Landlord",
    body: "Search for the landlord by their ID number. If you are the landlord yourself, search for your own ID.",
    next: "Got it →",
  },
  {
    id: "property-name",
    type: "info",
    phase: "Add Property",
    target: '[data-tutorial="property-name"]',
    title: "Property Name",
    body: "Give your property a name — e.g. \"Sunrise Apartments\", \"Kiambu Complex\". This is how it will appear in the system.",
    next: "Got it →",
  },
  {
    id: "property-city",
    type: "info",
    phase: "Add Property",
    target: '[data-tutorial="property-city"]',
    title: "City / Town",
    body: "Which city or town is the property in?",
    next: "Got it →",
  },
  {
    id: "property-location",
    type: "info",
    phase: "Add Property",
    target: '[data-tutorial="property-location"]',
    title: "Area / Neighbourhood",
    body: "The specific area — e.g. \"Westlands\", \"Kilimani\", \"Thika Road\".",
    next: "Got it →",
  },
  {
    id: "property-submit",
    type: "action",
    phase: "Add Property",
    target: '[data-tutorial="property-submit"]',
    title: "Create the Property",
    body: "Once all fields are filled in, click this button to save your property.",
    delay: 1200,
  },

  // ── Intermediate: open the property's units view ─────────────────────────────
  {
    id: "view-units-btn",
    type: "action",
    phase: "Add Units",
    target: '[data-tutorial="view-units-btn"]',
    title: "Open Your Property",
    body: "Your property has been created! Click \"View Units\" to open it and start adding units.",
    delay: 600,
  },

  // ── Phase 4: Add unit ────────────────────────────────────────────────────────
  {
    id: "add-unit-btn",
    type: "action",
    phase: "Add Units",
    target: '[data-tutorial="add-unit-btn"]',
    title: "Add a Unit",
    body: "A unit is an individual space tenants will rent — an apartment, room, shop, or office. Click \"Add Unit\".",
    delay: 600,
  },
  {
    id: "unit-label",
    type: "info",
    phase: "Add Units",
    target: '[data-tutorial="unit-label"]',
    title: "Unit Name / Number",
    body: "Give this unit a label so you can identify it. For example: \"Apt 2A\", \"Room 5\", \"Shop B\".",
    next: "Got it →",
  },
  {
    id: "unit-type",
    type: "info",
    phase: "Add Units",
    target: '[data-tutorial="unit-type"]',
    title: "Unit Type",
    body: "What kind of unit is this? Choose from the list — Studio, 1 Bedroom, 2 Bedrooms, Shop, Office, etc.",
    next: "Got it →",
  },
  {
    id: "unit-rent",
    type: "info",
    phase: "Add Units",
    target: '[data-tutorial="unit-rent"]',
    title: "Monthly Rent",
    body: "How much rent will the tenant pay each month? Enter the amount in Kenyan Shillings.",
    next: "Got it →",
  },
  {
    id: "unit-submit",
    type: "action",
    phase: "Add Units",
    target: '[data-tutorial="unit-submit"]',
    title: "Save the Unit",
    body: "Click \"Add Unit\" to save. You can add as many units as the property has.",
    delay: 800,
  },

  // ── Phase 5: Assign tenant ───────────────────────────────────────────────────
  {
    id: "open-unit-hint",
    type: "info",
    phase: "Assign Tenant",
    target: null,
    title: "Almost there! One more step.",
    body: "Your unit has been created! Now click on it in the list to open its details — then we'll assign your first tenant.",
    next: "Okay →",
  },
  {
    // Silent wait — polls for the assign button to appear (user opens unit modal)
    id: "wait-for-assign",
    type: "wait",
    phase: "Assign Tenant",
    target: '[data-tutorial="assign-btn"]',
  },
  {
    id: "assign-btn",
    type: "action",
    phase: "Assign Tenant",
    target: '[data-tutorial="assign-btn"]',
    title: "Assign a Tenant",
    body: "Found it! Click \"Assign\" to start the process of linking a tenant to this unit.",
    delay: 600,
  },
  {
    id: "tenant-search",
    type: "info",
    phase: "Assign Tenant",
    target: '[data-tutorial="tenant-search"]',
    title: "Find Your Tenant",
    body: "Search for your tenant by their ID number. If they don't have an account yet, click \"Create account\" to register them right here.",
    next: "Got it →",
  },
  {
    id: "tenant-submit",
    type: "info",
    phase: "Assign Tenant",
    target: '[data-tutorial="tenant-submit"]',
    title: "Complete the Assignment",
    body: "Before clicking Assign, make sure the tenant's wallet has enough balance (deposit + first month's rent). Top up via M-Pesa: Paybill 4123905, Account = their ID number.",
    next: "Got it, I'm ready →",
  },

  // ── Done ─────────────────────────────────────────────────────────────────────
  {
    id: "done",
    type: "done",
    phase: null,
    target: null,
    title: "You did it! 🎉",
    body: "Your property management system is fully set up. You've created a company, added a property, created units, and assigned a tenant. Welcome to RecordLOGS!",
    next: "Finish",
  },
]

export const STEPS_BY_ROLE = {
  propertyManager: PM_STEPS,
}
